// src/lib/useDiseaseRankingKcode.ts
// 疾患タブ最下部「点数表区分番号」 表 6/7/8 (手術 / 手術・処置等 1 / 手術・処置等 2) 用 hook。
// type ('ope' / 'proc1' / 'proc2') を渡して /api/query/disease_ranking_kcode を呼ぶ。
//
// 期間・dep・iryo・yotei・dpcfy・admdrcd・dpc6 は他の表と同じ filter を引き継ぐ
// (= 表 1 / 表 2-5 と数値を揃える)。
import { useQuery } from "@tanstack/react-query";
import { useFilters } from "../store";
import { diseasePeriod } from "./useDiseaseRanking";
import type { DiseaseRankingKcodeResponse, KcodeType } from "@shared/api";
import { GP_MODE_HEADER } from "./gpMode";

export function useDiseaseRankingKcode(type: KcodeType, enabled = true, branchOnly = false) {
  const f = useFilters();
  const { dateFrom: defFrom, dateTo: defTo } = diseasePeriod(f.asOfMonth, f.dpcfy, f.diseaseAutoPeriod);
  const dateFrom = f.dateFromOverride ?? defFrom;
  const dateTo   = f.dateToOverride   ?? defTo;
  const dep = f.focusedDept;
  const granularity = f.granularity;
  const dpcfy = f.dpcfy;
  const dpc6  = f.dpc6;
  const yotei = f.diseaseYotei === "合計" ? null : f.diseaseYotei;
  const iryo  = f.diseaseIryo  === "合計" ? null : f.diseaseIryo;
  // 主表で選択中の DPC 階層。 14 → 12 → 11 → 10 の順で「最も深い 1 階層のみ」 を送る。
  // [絞り込み保持 2026-07-17] 階層切替で選択を保持するようになったため、
  // 「表示階層以下の絞り込みは上位階層に反映させない」よう dpcLevel でゲートしてから最深を選ぶ
  // (例: 処置2別で選択後に疾患別へ戻ったら、処置2の絞り込みは kcode 表にも適用しない)。
  const lvl = f.dpcLevel;
  // [7/8/13桁 2026-09-28] 7/8 桁の選択は selectedDpc10 に、13 桁の選択は selectedDpc12 に入れて送る
  //   (server は前方一致なので桁数が短くても長くても正しく絞れる)。
  const g7  = lvl >= 7  ? f.selectedDpc7  : null;
  const g8  = lvl >= 8  ? f.selectedDpc8  : null;
  const g10 = (lvl >= 10 ? f.selectedDpc10 : null) ?? g8 ?? g7;
  const g11 = lvl >= 11 ? f.selectedDpc11 : null;
  const g12 = (lvl >= 13 ? f.selectedDpc13 : null) ?? (lvl >= 12 ? f.selectedDpc12 : null);
  const g14 = lvl >= 14 ? f.selectedDpc14 : null;
  const selectedDpc10 = g14 || g12 || g11 ? null : (g10 && g10.length >= 7 ? g10 : null);
  const selectedDpc11 = g14 || g12 ? null : g11;
  const selectedDpc12 = g14 ? null : g12;
  const selectedDpc14 = g14;

  // [分岐該当のみ 2026-07-18] トグル ON のとき、選択中の 11/12 桁分岐を候補K フィルタ用に送る
  const branch11 = branchOnly && type === "proc1" ? g11 : null;
  const branch12 = branchOnly && type === "proc2" && g12 ? g12.slice(0, 12) : null;
  return useQuery({
    queryKey: [
      "disease_ranking_kcode",
      type, dateFrom, dateTo, dep, granularity, dpcfy, dpc6, f.admdrcd, yotei, iryo,
      selectedDpc10, selectedDpc11, selectedDpc12, selectedDpc14,
      branchOnly, branch11, branch12, lvl,
    ],
    queryFn: async () => {
      const r = await fetch("/api/query/disease_ranking_kcode", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...GP_MODE_HEADER },
        body: JSON.stringify({
          type,
          dateFrom, dateTo,
          dep, granularity, dpcfy, dpc6, admdrcd: f.admdrcd,
          yotei, iryo,
          selectedDpc10, selectedDpc11, selectedDpc12, selectedDpc14,
          branchOnly, branch11, branch12,
          level: lvl,   // [2026-09-29] 主表と同じ対象退院 (7 桁以上は dpc14 未確定を除く)
        }),
      });
      if (!r.ok) throw new Error(`disease_ranking_kcode ${r.status}`);
      return r.json() as Promise<DiseaseRankingKcodeResponse>;
    },
    enabled,
    placeholderData: (prev) => prev,
    staleTime: 60_000,
  });
}
